import { Link } from 'react-router-dom';
import { useInView } from '../../../hooks/useInView';

const services = [
  {
    icon: 'ri-movie-2-fill',
    number: '01',
    title: 'Corporate Video Production',
    tagline: 'Content engineered to convert',
    keyword: 'Corporate Video Production',
    desc: 'From 30-second broadcast spots to full campaign series — we produce corporate video production at broadcast quality that captures attention, communicates your value, and drives measurable action.',
    img: 'https://public.readdy.ai/ai/img_res/111055e40554bfc56d9b55c598e4e16b.jpg',
  },
  {
    icon: 'ri-film-fill',
    number: '02',
    title: 'Brand Story Films',
    tagline: 'Your story is your greatest asset',
    keyword: 'Brand Story Films',
    desc: 'Cinematic brand documentaries that humanize your organization, communicate your mission, and forge deep emotional connections with audiences who matter most to your growth and authority.',
    img: 'https://readdy.ai/api/search-image?query=beautiful%20cinematic%20brand%20documentary%20interview%20setup%20elegant%20subject%20in%20director%20chair%20warm%20bokeh%20background%20soft%20key%20light%20dark%20studio%20rich%20background%20high%20end%20storytelling%20atmosphere%20authentic%20emotional%20depth%20documentary%20film&width=900&height=1000&seq=sv3_002&orientation=portrait',
  },
  {
    icon: 'ri-broadcast-fill',
    number: '03',
    title: 'Live Streaming Services',
    tagline: 'Broadcast-grade. Every time.',
    keyword: 'Live Streaming Services',
    desc: 'Professional live streaming services for events, church services, conferences, and brand activations. Multi-camera, broadcast-grade real-time production streamed globally to any platform.',
    img: 'https://readdy.ai/api/search-image?query=professional%20broadcast%20live%20streaming%20production%20control%20room%20multiple%20large%20screens%20video%20switcher%20waveform%20monitors%20dark%20technical%20atmosphere%20teal%20blue%20glow%20professional%20television%20broadcast%20operators%20camera%20professionals%20high%20end%20equipment&width=900&height=1000&seq=sv3_003&orientation=portrait',
  },
  {
    icon: 'ri-live-fill',
    number: '04',
    title: 'Event Coverage',
    tagline: 'Never miss a defining moment',
    keyword: 'Event Video Coverage',
    desc: 'Multi-camera event production that captures the energy, emotion, and defining moments of your most important gatherings. Delivered in broadcast production quality for maximum replay value.',
    img: 'https://readdy.ai/api/search-image?query=massive%20professional%20event%20stage%20production%20cinematic%20dramatic%20beam%20lights%20crowd%20silhouettes%20dark%20spectacular%20venue%20concert%20professional%20multi%20camera%20broadcast%20coverage%20high%20contrast%20premium%20production%20energy&width=900&height=1000&seq=sv3_004&orientation=portrait',
  },
  {
    icon: 'ri-camera-3-fill',
    number: '05',
    title: 'Photography Services',
    tagline: 'Images that stop the scroll',
    keyword: 'Commercial Photography',
    desc: 'Commercial photography, executive headshots, and editorial imagery crafted to represent your brand with authority, precision, and visual impact across every platform and campaign.',
    img: 'https://readdy.ai/api/search-image?query=luxury%20editorial%20commercial%20photography%20studio%20dramatic%20directional%20strobe%20lighting%20dark%20rich%20background%20professional%20photographer%20shooting%20high%20end%20editorial%20fashion%20subject%20beautiful%20light%20shaping%20moody%20cinematic%20atmosphere&width=900&height=1000&seq=sv3_005&orientation=portrait',
  },
  {
    icon: 'ri-scissors-2-fill',
    number: '06',
    title: 'Creative Direction & Editing',
    tagline: 'Where raw footage becomes legacy',
    keyword: 'Post Production Editing',
    desc: 'World-class color grading, sound design, motion graphics, and editorial strategy that transforms raw footage into a polished cinematic asset ready to perform across digital and broadcast channels.',
    img: 'https://readdy.ai/api/search-image?query=professional%20post%20production%20editing%20suite%20dark%20room%20large%20curved%20monitors%20DaVinci%20Resolve%20color%20grading%20software%20vibrant%20screen%20glow%20deep%20blacks%20neon%20ambient%20lighting%20premium%20workstation%20high%20end%20cinematic%20post%20production&width=900&height=1000&seq=sv3_006&orientation=portrait',
  },
];

interface ServiceCardProps {
  svc: typeof services[0];
  index: number;
}

function ServiceCard({ svc, index }: ServiceCardProps) {
  const { ref, inView } = useInView();
  const isEven = index % 2 === 0;

  return (
    <article
      ref={ref as React.RefObject<HTMLDivElement>}
      id={svc.keyword.toLowerCase().replace(/\s+/g, '-')}
      className={`relative grid grid-cols-1 lg:grid-cols-2 transition-all duration-1000 ${
        inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
      }`}
      style={{ transitionDelay: '80ms' }}
    >
      {/* Image */}
      <div className={`relative overflow-hidden ${!isEven ? 'lg:order-2' : ''}`} style={{ minHeight: '500px' }}>
        <img
          src={svc.img}
          alt={svc.title}
          className="absolute inset-0 w-full h-full object-cover object-top transition-transform duration-[2s] ease-out hover:scale-[1.04]"
        />
        {/* Depth overlap gradients */}
        <div className={`absolute inset-0 ${isEven ? 'bg-gradient-to-r from-transparent via-transparent to-[#0c0c0c]/85' : 'bg-gradient-to-l from-transparent via-transparent to-[#0c0c0c]/85'}`} />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0c0c0c]/30 via-transparent to-[#0c0c0c]/50" />
        {/* Number ghost */}
        <div className={`absolute top-8 ${isEven ? 'right-8' : 'left-8'} font-serif font-bold leading-none text-white/4`} style={{ fontSize: '8rem' }}>{svc.number}</div>
        {/* Bottom tagline badge */}
        <div className="absolute bottom-0 left-0 right-0 p-6">
          <div className="flex items-center gap-3">
            <div className="w-7 h-7 flex items-center justify-center bg-gold/10 backdrop-blur-sm border border-gold/30">
              <i className={`${svc.icon} text-gold text-xs`} />
            </div>
            <span className="font-sans text-[0.58rem] tracking-[0.3em] uppercase text-gold/70 backdrop-blur-sm">{svc.tagline}</span>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className={`relative flex flex-col justify-center p-12 lg:p-16 xl:p-20 bg-[#0c0c0c] ${!isEven ? 'lg:order-1' : ''}`}>
        {/* Accent line top */}
        <div className={`absolute top-0 ${isEven ? 'left-0 right-0' : 'left-0 right-0'} h-px bg-gradient-to-r from-gold/20 via-gold/5 to-transparent`} />

        <div className="flex items-center gap-3 mb-4">
          <span className="font-sans text-[0.55rem] tracking-[0.4em] uppercase text-gold/50 font-medium">{svc.number}</span>
          <div className="w-6 h-px bg-gold/30" />
          <span className="font-sans text-[0.55rem] tracking-[0.3em] uppercase text-white/25">{svc.keyword}</span>
        </div>

        <h2 className="font-serif text-3xl lg:text-4xl xl:text-[2.6rem] font-bold text-white leading-tight mb-5">{svc.title}</h2>
        <div className="flex items-center gap-3 mb-7">
          <div className="w-10 h-px bg-gold" />
          <div className="w-2 h-px bg-gold/40" />
        </div>
        <p className="font-sans text-sm text-white/50 leading-[2] mb-10 max-w-md">{svc.desc}</p>

        <div className="flex items-center gap-4">
          <Link
            to="/contact"
            className="whitespace-nowrap group inline-flex items-center gap-3 font-sans text-[0.65rem] tracking-[0.2em] uppercase px-8 py-4 bg-gold/10 border border-gold/40 text-gold hover:bg-gold hover:text-[#020202] hover:border-gold transition-all duration-300 cursor-pointer"
          >
            Get a Quote
            <i className="ri-arrow-right-line group-hover:translate-x-1 transition-transform" />
          </Link>
          <Link
            to="/services"
            className="whitespace-nowrap font-sans text-[0.65rem] tracking-[0.2em] uppercase text-white/30 hover:text-gold transition-colors duration-300 cursor-pointer"
          >
            Learn More
          </Link>
        </div>
      </div>
    </article>
  );
}

export default function ServicesOverview() {
  const { ref, inView } = useInView();

  return (
    <section className="relative bg-[#0c0c0c]" id="services">
      {/* Section header */}
      <div className="pt-32 pb-20 max-w-7xl mx-auto px-8 lg:px-20">
        <div
          ref={ref as React.RefObject<HTMLDivElement>}
          className={`flex flex-col md:flex-row md:items-end justify-between gap-10 transition-all duration-800 ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
        >
          <div>
            <p className="font-sans text-[0.58rem] tracking-[0.45em] uppercase text-gold mb-6 font-medium">What We Do</p>
            <h2 className="font-serif font-bold text-white leading-none" style={{ fontSize: 'clamp(3rem, 5.5vw, 5.5rem)', letterSpacing: '-0.025em' }}>
              Premium Production<br />
              <em className="not-italic">
                <span className="text-gold">Services</span>
              </em>
            </h2>
          </div>
          <div className="max-w-sm">
            <div className="w-8 h-px bg-gold/40 mb-5" />
            <p className="font-sans text-sm text-white/40 leading-[1.9]">
              Six broadcast-grade production disciplines. One unwavering commitment — making your brand impossible to ignore across every platform and channel.
            </p>
          </div>
        </div>
      </div>

      {/* Service cards — full-bleed alternating */}
      <div className="flex flex-col" data-product-shop>
        {services.map((svc, i) => (
          <ServiceCard key={svc.title} svc={svc} index={i} />
        ))}
      </div>
    </section>
  );
}
